import { useState, useEffect } from "react";
import { X, UserPlus, Users, Trash2, Share2, Phone, Heart, Sun, Bell, KeyRound, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";
import { isAuthed } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FamilyPanel({ sessionId, profile, onClose, onMessageUpdate }) {
  const [contacts, setContacts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [relation, setRelation] = useState("Son/Daughter");
  const [promoteFor, setPromoteFor] = useState(null);
  const [promotePin, setPromotePin] = useState("");
  const [promoting, setPromoting] = useState(false);
  const canPromote = isAuthed() && profile?.role !== "family";

  // Daily check-in settings
  const [checkinEnabled, setCheckinEnabled] = useState(false);
  const [checkinTime, setCheckinTime] = useState("08:00");
  const [checkinSaving, setCheckinSaving] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/checkins/status/${sessionId}`)
      .then((r) => {
        setCheckinEnabled(!!r.data.enabled);
        if (r.data.time) setCheckinTime(r.data.time);
      })
      .catch(() => {});
  }, [sessionId]);

  const toggleCheckin = async (next) => {
    setCheckinSaving(true);
    try {
      if (next) {
        await axios.post(`${API}/checkins/enable`, { session_id: sessionId, time: checkinTime });
        setCheckinEnabled(true);
        toast.success(`Daily check-in enabled at ${checkinTime} IST`);
      } else {
        await axios.post(`${API}/checkins/disable`, { session_id: sessionId });
        setCheckinEnabled(false);
        toast.success("Daily check-in disabled");
      }
    } catch {
      toast.error("Could not update check-in setting");
    } finally {
      setCheckinSaving(false);
    }
  };

  const updateCheckinTime = async (newTime) => {
    setCheckinTime(newTime);
    if (!checkinEnabled) return;
    try {
      await axios.post(`${API}/checkins/enable`, { session_id: sessionId, time: newTime });
      toast.success(`Check-in time updated to ${newTime} IST`);
    } catch {
      toast.error("Could not update time");
    }
  };

  const RELATIONS = ["Son/Daughter", "Spouse", "Grandchild", "Sibling", "Caretaker", "Friend", "Other"];

  const loadContacts = async () => {
    try {
      const res = await axios.get(`${API}/family/${sessionId}`);
      setContacts(res.data.contacts || []);
      setLoaded(true);
    } catch (e) {
      console.error("Failed to load contacts:", e);
    }
  };

  if (!loaded) loadContacts();

  const addContact = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter name and phone number");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      formData.append("name", name);
      formData.append("phone", phone);
      formData.append("relation", relation);
      const res = await axios.post(`${API}/family/save`, formData);
      setContacts((prev) => [...prev, res.data]);
      setName("");
      setPhone("");
      setShowAdd(false);
      toast.success(`${name} added as family contact`);
    } catch (e) {
      toast.error("Could not save contact");
    }
  };

  const deleteContact = async (contactId, contactName) => {
    try {
      await axios.delete(`${API}/family/${contactId}`);
      setContacts((prev) => prev.filter((c) => c.id !== contactId));
      toast.success(`${contactName} removed`);
    } catch (e) {
      toast.error("Could not remove contact");
    }
  };

  const promoteContact = async () => {
    if (!/^\d{4}$/.test(promotePin)) {
      toast.error("Please enter a 4-digit PIN for them.");
      return;
    }
    setPromoting(true);
    try {
      await axios.post(
        `${API}/family/promote/${promoteFor.id}`,
        { pin: promotePin }
      );
      setContacts((prev) => prev.map((c) => (c.id === promoteFor.id ? { ...c, has_login: true } : c)));
      toast.success(`${promoteFor.name} can now log in as a family caretaker`);
      setPromoteFor(null);
      setPromotePin("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set up family login.");
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="family-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 md:px-6 md:py-4 shrink-0" style={{ backgroundColor: "#2D2766" }}>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-white" />
            <h2 className="text-white font-semibold text-lg">Family Contacts</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="close-family-btn">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Info banner */}
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-3">
          <p className="text-sm text-emerald-800 leading-relaxed">
            Add family members to share your order details and keep them informed about your requests.
          </p>
        </div>

        {/* Daily Check-in settings */}
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3.5" data-testid="checkin-section">
          <div className="flex items-start gap-3">
            <div className="p-1.5 bg-amber-100 rounded-lg mt-0.5">
              <Sun className="w-4 h-4 text-amber-700" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-amber-900">Daily Check-in</p>
                <button
                  role="switch"
                  aria-checked={checkinEnabled}
                  disabled={checkinSaving}
                  onClick={() => toggleCheckin(!checkinEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checkinEnabled ? "bg-emerald-500" : "bg-gray-300"}`}
                  data-testid="checkin-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checkinEnabled ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
              <p className="text-xs text-amber-800 leading-relaxed mt-1">
                We'll ask how you're feeling every day. If you don't respond within 6 hours, your family will be alerted.
              </p>
              {checkinEnabled && (
                <div className="mt-2 flex items-center gap-2">
                  <Bell className="w-3.5 h-3.5 text-amber-700" />
                  <label className="text-xs text-amber-900">Time (IST):</label>
                  <input
                    type="time"
                    value={checkinTime}
                    onChange={(e) => updateCheckinTime(e.target.value)}
                    className="text-xs px-2 py-1 border border-amber-300 rounded bg-white"
                    data-testid="checkin-time-input"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add contact form */}
        {showAdd ? (
          <div className="p-4 bg-gray-50 border-b" data-testid="add-family-form">
            <h3 className="font-medium text-gray-900 mb-3">Add Family Member</h3>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-[#2D2766]"
                data-testid="family-name-input"
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-[#2D2766]"
                data-testid="family-phone-input"
              />
              <select
                value={relation}
                onChange={(e) => setRelation(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-[#2D2766] bg-white"
                data-testid="family-relation-select"
              >
                {RELATIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button
                  onClick={addContact}
                  className="flex-1 bg-[#E8B830] text-white py-2.5 rounded-lg font-medium hover:bg-[#3D3580] transition-colors"
                  data-testid="save-family-btn"
                >
                  Save Contact
                </button>
                <button
                  onClick={() => setShowAdd(false)}
                  className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-3 px-4 py-3.5 border-b hover:bg-gray-50 transition-colors w-full text-left"
            data-testid="add-family-btn"
          >
            <div className="w-10 h-10 rounded-full bg-[#E8B830] flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
            <span className="text-base font-medium text-[#2D2766]">Add Family Member</span>
          </button>
        )}

        {/* Contacts list */}
        <div className="flex-1 overflow-y-auto">
          {contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3 px-8">
              <Heart className="w-12 h-12" />
              <p className="text-lg text-center">No family members added yet</p>
              <p className="text-sm text-center">
                Add your son, daughter, or caretaker so they stay informed about your orders.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="px-4 py-3 hover:bg-gray-50"
                  data-testid={`family-contact-${contact.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#3D3580] flex items-center justify-center text-white font-bold">
                        {contact.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-base">{contact.name}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Phone className="w-3 h-3" />
                          <span>{contact.phone}</span>
                          <Badge className="text-xs bg-gray-100 text-gray-600 border-0">
                            {contact.relation}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteContact(contact.id, contact.name)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      data-testid={`delete-family-${contact.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {canPromote && (
                    contact.has_login ? (
                      <p className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium mt-2 ml-13" data-testid={`family-login-active-${contact.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Has family login
                      </p>
                    ) : promoteFor?.id === contact.id ? (
                      <div className="mt-3 ml-1 p-3 bg-indigo-50 rounded-xl" data-testid={`promote-form-${contact.id}`}>
                        <p className="text-xs text-[#2D2766] font-medium mb-2">Set a 4-digit PIN for {contact.name} to log in with:</p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={4}
                            value={promotePin}
                            onChange={(e) => setPromotePin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                            placeholder="1234"
                            className="flex-1 min-w-0 px-3 py-2 text-center text-lg font-bold border-2 border-[#2D2766]/30 rounded-lg focus:outline-none focus:border-[#2D2766]"
                            data-testid={`promote-pin-input-${contact.id}`}
                          />
                          <button
                            onClick={promoteContact}
                            disabled={promoting}
                            className="px-3 py-2 bg-[#2D2766] text-white rounded-lg text-sm font-semibold disabled:opacity-60"
                            data-testid={`promote-confirm-${contact.id}`}
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => { setPromoteFor(null); setPromotePin(""); }}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600"
                            data-testid={`promote-cancel-${contact.id}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setPromoteFor(contact)}
                        className="flex items-center gap-1.5 text-xs font-semibold text-[#2D2766] mt-2 hover:underline"
                        data-testid={`promote-btn-${contact.id}`}
                      >
                        <KeyRound className="w-3.5 h-3.5" /> Give Family Login
                      </button>
                    )
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
